import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export type Lang = "en" | "es";

type Dict = Record<string, string>;

const en: Dict = {
  // NAV
  "nav.home": "Home",
  "nav.services": "Services",
  "nav.about": "About",
  "nav.work": "Our Work",
  "nav.faq": "FAQ",
  "nav.blog": "Blog",
  "nav.contact": "Contact",
  "nav.callNow": "Call Now",
  "nav.requestConsultation": "Request Consultation",
  "nav.callFull": "Call (505) 868-0847",

  // COMMON
  "common.callNow": "Call Now",
  "common.callPhone": "Call (505) 868-0847",
  "common.requestConsultation": "Request Consultation",
  "common.getFreeEstimate": "Get an Estimate",
  "common.requestQuote": "Request Quote",
  "common.startProject": "Start Your Project",
  "common.viewServices": "View Our Services",
  "common.readyTransform": "Ready to transform your roof?",
  "home.cta.title": "Ready to transform your roof?",
  "work.cta.title": "Love what you see? Let's build yours.",
  "about.cta.title": "Ready to work with a team you can trust?",
  "faq.cta.title": "Still have questions? We're here to help.",
  "common.emailUs": "Email Us",
  "common.licensedInsured": "Licensed & Insured",
  "common.manufacturerWarranties": "Manufacturer Warranties",
  "common.emergency247": "24/7 Emergency Response",
  "common.yearsBadge": "25+ Years • New Mexico",
  "common.ourStory": "Our Story",

  // HOME — about section
  "home.about.badge": "About AMR 3",
  "home.about.title": "About Us",
  "home.about.sub": "One trusted partner for roofing, solar, and HVAC across New Mexico.",
  "home.about.body":
    "One trusted, owner-operated partner for roofing and solar across New Mexico — delivering quality, accountability, and long-term value from consultation to completion.",

  // HERO (HOME)
  "hero.h1": "Expert Roofing & Solar Integration in New Mexico",
  "hero.sub":
    "Protect your home, reduce energy costs, and increase property value with our seamless, all-in-one roofing and solar solutions. Over 25 years of trusted craftsmanship.",
  "hero.cta1": "Request a Consultation",
  "hero.cta2": "Call 1-505-868-0847",

  // HOME — services grid
  "home.services.title": "Our Services",
  "home.services.sub": "Comprehensive roofing and solar solutions — all under one trusted contractor.",
  "home.services.learnAbout": "Learn about",
  "home.svc.1.title": "Roof Installation & Replacement",
  "home.svc.1.body": "Premium asphalt, metal, tile and TPO systems engineered for New Mexico weather, installed to last decades.",
  "home.svc.2.title": "Roof Repairs & Maintenance",
  "home.svc.2.body": "Fast, reliable repairs and seasonal maintenance plans that protect your roof and extend its lifespan.",
  "home.svc.3.title": "Solar Panel Installation",
  "home.svc.3.body": "High-efficiency panels and inverters designed and permitted in-house for maximum savings and performance.",
  "home.svc.4.title": "Integrated Solar Roofing",
  "home.svc.4.body": "One coordinated build for roof and solar — lower cost, fewer delays, and a single warranty owner.",
  "home.svc.5.title": "Storm Damage & Emergency",
  "home.svc.5.body": "24/7 emergency response, tarping, and full storm damage restoration with insurance claim assistance.",
  "home.svc.6.title": "Gutters & Insurance Claims",
  "home.svc.6.body": "Seamless gutter installation and end-to-end insurance claim support so nothing falls through the cracks.",
  "home.svc.7.title": "HVAC Installation & Service",
  "home.svc.7.body": "Energy-efficient heating, cooling, and ventilation systems — installed, repaired, and tuned for New Mexico's desert climate.",

  // HOME — process
  "home.how.title": "How We Work",
  "home.how.sub": "From the first consultation to warranty activation, we make roofing and solar simple.",
  "home.step.1.title": "Consultation & Site Visit",
  "home.step.1.body": "We meet on site, listen to your goals, and assess your roof and solar needs — no pressure, no obligation.",
  "home.step.2.title": "Detailed Proposal & Estimate",
  "home.step.2.body": "You receive a clear, line-item proposal with materials, timeline, warranty terms and transparent pricing.",
  "home.step.3.title": "Permits & Preparation",
  "home.step.3.body": "We handle every permit, HOA submission and utility interconnection so your project moves forward smoothly.",
  "home.step.4.title": "Seamless Installation",
  "home.step.4.body": "Our in-house roofing and solar crews execute the build on schedule, on budget, and to manufacturer spec.",
  "home.step.5.title": "Final Inspection & Cleanup",
  "home.step.5.body": "We walk the project with you, verify every detail, and leave the property cleaner than we found it.",
  "home.step.6.title": "Warranty Activation & Support",
  "home.step.6.body": "Workmanship and manufacturer warranties are activated and we stay available for the long haul.",

  // HOME — why choose
  "home.why.title": "Why Choose AMR 3",
  "home.why.sub": "We deliver premium roofing and solar with the craftsmanship, accountability, and warranties New Mexico families deserve.",
  "home.why.1.title": "25+ Years of NM Experience",
  "home.why.1.body": "Decades of hands-on roofing and solar work across Albuquerque and the entire state of New Mexico.",
  "home.why.2.title": "Licensed, Bonded & Insured",
  "home.why.2.body": "Fully licensed New Mexico contractor with comprehensive liability and workers' compensation coverage.",
  "home.why.3.title": "Owner-Operated Quality",
  "home.why.3.body": "Every project is overseen by an owner — never handed off to subcontractors you've never met.",
  "home.why.4.title": "Manufacturer-Certified Installer",
  "home.why.4.body": "Certified to install premium roofing and solar systems with full manufacturer warranty backing.",
  "home.why.5.title": "Leak-Free & Performance Guarantee",
  "home.why.5.body": "Workmanship guarantee on every roof and a performance guarantee on every solar system we install.",

  // HOME — reviews
  "home.reviews.title": "Google Reviews",
  "home.reviews.sub": "See what homeowners across New Mexico say about working with AMR 3.",
  "home.reviews.seeAll": "See all reviews on Google",

  // HOME — work in action
  "home.work.title": "See Our Work in Action",
  "home.work.body1": "Watch how our team delivers premium roofing and solar projects across New Mexico — from the first tear-off to the final panel activation. Real crews, real homes, real craftsmanship.",
  "home.work.body2": "We treat every project like it's our own home — that's the AMR 3 difference, and it shows in every roof and solar system we install.",
  "home.work.playLabel": "Play project video",

  // HOME — location
  "home.loc.title": "Our Location",

  // ABOUT
  "about.badge": "Owner-Operated · 25+ Years in NM",
  "about.h1": "About AMR 3 Design & Construction",
  "about.intro": "AMR 3 Design & Construction LLC was founded to give New Mexico homeowners and businesses a simplified, professional approach to roofing and solar integration. Instead of juggling multiple contractors, customers work with one experienced, owner-operated team from first consultation to final warranty activation.",
  "about.story.title": "Our Story",
  "about.story.sub": "From a single vision to a trusted name in New Mexico roofing and solar.",
  "about.story.1.lead": "AMR 3 Design & Construction LLC",
  "about.story.1.body": "was founded to give New Mexico homeowners and businesses a single, trusted partner for premium roofing and solar — no juggling contractors, no mismatched warranties, no surprises.",
  "about.story.2.lead": "What sets us apart is our owner-operated approach.",
  "about.story.2.body": "Every project is overseen by an owner from estimate to final walkthrough — clients work directly with the people doing the work, not a call-center middleman.",
  "about.story.3.lead": "We focus on premium materials, certified craftsmanship, and clear communication",
  "about.story.3.body": "so customers can confidently invest in their roof and solar system. Our integrated process delivers a beautiful, energy-efficient result built to last decades in New Mexico's climate.",
  "about.story.paragraph": "AMR 3 Design & Construction LLC was founded to give New Mexico homeowners and businesses a single, trusted partner for premium roofing and solar — no juggling contractors, no mismatched warranties, no surprises. Every project is owner-operated and overseen from estimate to final walkthrough, so you work directly with the people doing the work. We focus on premium materials, certified craftsmanship, and clear communication, delivering a beautiful, energy-efficient result built to last decades in New Mexico's climate.",
  "about.story.badges.years": "25+ Years Experience",
  "about.story.badges.owner": "Owner-Operated",
  "about.story.badges.licensed": "Licensed",
  "about.story.badges.bonded": "Bonded",
  "about.story.badges.insured": "Insured",
  "about.apart.title": "What Sets Us Apart",
  "about.apart.sub": "There are plenty of roofing and solar companies out there. Here's why homeowners and businesses across New Mexico choose AMR 3.",
  "about.apart.1.title": "One-Stop Roofing & Solar",
  "about.apart.1.body": "Instead of coordinating separate roofers, solar installers and electricians, you get one experienced team that handles roof, solar and finish from start to finish.",
  "about.apart.2.title": "Owner-Operated Quality",
  "about.apart.2.body": "Every project is led by an owner — never handed off to subcontractors you've never met. The same standards on every job, every time.",
  "about.apart.3.title": "25+ Years of NM Experience",
  "about.apart.3.body": "Decades of hands-on roofing and solar work across Albuquerque, Rio Rancho, Santa Fe and the rest of New Mexico — built for our climate.",
  "about.apart.4.title": "Licensed, Bonded & Insured",
  "about.apart.4.body": "Fully licensed New Mexico contractor with comprehensive liability and workers' compensation coverage on every project.",
  "about.apart.5.title": "Integrated Solar Specialists",
  "about.apart.5.body": "Manufacturer-certified solar installers who design, permit and install your system as one coordinated build with your roof.",
  "about.commit.title": "Our Commitment & Expertise",
  "about.commit.cs.title": "Customer Satisfaction Focused",
  "about.commit.cs.body": "We stand behind every project with manufacturer-certified workmanship, accurate written estimates, coordinated project scheduling, and premium materials engineered for New Mexico weather.",
  "about.commit.partner.lead": "Manufacturer partnerships",
  "about.commit.partner.body": "with leading roofing and solar suppliers ensure the highest quality systems and full warranty backing on every installation.",
  "about.commit.roof.title": "Roofing Expertise",
  "about.commit.roof.body": "Specialized residential and commercial roof installation experience.",
  "about.commit.solar.title": "Solar Integration",
  "about.commit.solar.body": "Manufacturer-certified solar specialists you can trust.",

  // SERVICES
  "svc.badge": "Roofing & Solar Specialists",
  "svc.h1.l1": "Roofing & Solar Services in",
  "svc.h1.l2": "Albuquerque & New Mexico",
  "svc.intro": "From premium roof installations and solar integration to storm damage restoration and gutters — we provide a streamlined building experience with professional craftsmanship. Serving Albuquerque, Rio Rancho, Santa Fe, Los Lunas and surrounding New Mexico communities.",
  "svc.primary.title": "Primary Services",
  "svc.primary.sub": "Our core roofing and solar solutions — designed, permitted, and installed in-house for New Mexico homes and businesses.",
  "svc.expect": "What to Expect:",
  "svc.p.1.title": "Roofing Installation & Replacement",
  "svc.p.1.body": "From premium asphalt shingles to metal, tile and TPO systems — we engineer, permit and install roofs built to last decades in New Mexico's sun, wind and monsoon weather.",
  "svc.p.1.expect": "Every project starts with a complimentary on-site assessment where we evaluate your existing roof, discuss material options, and provide a clear written proposal. Our in-house crews handle tear-off, decking, underlayment, flashing and finish — backed by manufacturer and workmanship warranties.",
  "svc.p.2.title": "Solar Panel Installation & Integration",
  "svc.p.2.body": "High-efficiency solar systems designed, permitted and installed by the same team that builds your roof — for a seamless finish, lower total cost, and a single warranty owner you can trust.",
  "svc.p.2.expect": "We start with a complimentary energy audit and custom system design tailored to your usage, roof orientation and budget. We coordinate utility interconnection, all permits and HOA submissions, then install premium panels, inverters and monitoring with a single point of accountability.",
  "svc.p.3.title": "Storm Damage & Emergency Repair",
  "svc.p.3.body": "24/7 emergency response, professional tarping and full restoration after hail, wind or monsoon damage — handled end-to-end so your home is protected and your claim moves quickly.",
  "svc.p.3.expect": "We respond fast, document the damage with photos and detailed reports, install emergency protection, and coordinate directly with your insurance adjuster. Our team manages the full restoration so you have one trusted point of contact from first call to final inspection.",
  "svc.add.title": "Additional Services",
  "svc.add.sub": "Beyond roofs and solar — we handle gutters, insurance claims and commercial systems too.",
  "svc.add.1.title": "Seamless Gutters",
  "svc.add.1.body": "Custom-fit seamless aluminum gutters, downspouts and leaf guards — installed and maintained to keep water away from your roof and foundation.",
  "svc.add.2.title": "Insurance Claim Assistance",
  "svc.add.2.body": "We document damage, meet with your adjuster, file paperwork and bill insurance directly — taking the stress out of the claims process.",
  "svc.add.3.title": "Commercial & Specialty Roofing",
  "svc.add.3.body": "Flat roofs, TPO systems, commercial reroofs, skylights and custom architectural details for businesses across New Mexico.",
  "svc.add.4.title": "HVAC Installation & Service",
  "svc.add.4.body": "Full-service HVAC: high-efficiency AC, heat pumps, furnaces and ductwork — designed, installed and maintained for New Mexico's climate.",
  "svc.coord": "Coordinated project scheduling — one team handles roofing, solar, and finishing.",
  "svc.commit.title": "Our Commitment",
  "svc.commit.body1": "Every project comes with our commitment to",
  "svc.commit.body1b": "licensed craftsmanship, accurate estimates, and premium materials",
  "svc.commit.body1c": ". We focus on customer satisfaction at every stage.",
  "svc.commit.quote": "\"Coordinated project scheduling and clear communication — so you can enjoy the result, not stress over it.\"",
  "svc.chip.years": "25+ Years of NM Experience",
  "svc.chip.licensed": "Licensed, Bonded & Insured",
  "svc.chip.certified": "Manufacturer Certified",
  "svc.cta.title": "Tell us about your roofing or solar project — we'll reply quickly.",

  // FAQ
  "faq.badge": "Answers · Roofing & Solar",
  "faq.h1": "Frequently Asked Questions",
  "faq.sub": "Everything you need to know about working with AMR 3 Design & Construction LLC for roofing and solar projects across New Mexico.",
  "faq.still": "Still have questions?",

  // WORK
  "work.badge": "Project Portfolio",
  "work.h1": "Our Work",
  "work.intro": "Browse real projects completed by AMR 3 Design & Construction — residential roofing, solar integrations, and commercial systems across Albuquerque, Rio Rancho, Santa Fe and the rest of New Mexico.",
  "work.tab.all": "All",
  "work.tab.roofing": "Roofing",
  "work.tab.solar": "Solar",
  "work.tab.repairs": "Repairs",
  "work.tab.commercial": "Commercial",
  "work.galleryTitle": "Project Gallery",
  "work.gallerySub": "Browse a curated selection of our completed roofing and solar projects across New Mexico.",
  "work.baTitle": "Before & After",
  "work.baSub": "Slide to see the real transformation on homes we've repaired, replaced, and upgraded.",

  // CONTACT
  "contact.badge": "Consultation · No Obligation",
  "contact.h1": "Request a Consultation",
  "contact.intro": "Fill out the form below or call us directly. We serve Albuquerque and surrounding New Mexico communities including Rio Rancho, Santa Fe, Los Lunas, Las Cruces and the East Mountains.",
  "contact.info.address": "Albuquerque, NM 87113",
  "contact.info.hours": "Mon–Fri 7AM–7PM · 24/7 Emergency",
  "contact.form.title": "Tell Us About Your Project",
  "contact.form.sub": "A real human will get back to you within one business day.",
  "contact.form.name": "Name",
  "contact.form.namePh": "Your full name",
  "contact.form.phone": "Phone",
  "contact.form.email": "Email",
  "contact.form.address": "Project Address",
  "contact.form.addressPh": "Street, City, NM",
  "contact.form.service": "Service Type",
  "contact.form.servicePh": "Choose a service (optional)",
  "contact.form.s.roofing": "Roofing Installation / Replacement",
  "contact.form.s.solar": "Solar Panel Installation",
  "contact.form.s.both": "Integrated Roof + Solar",
  "contact.form.s.repair": "Roof Repair / Maintenance",
  "contact.form.s.storm": "Storm Damage / Insurance Claim",
  "contact.form.s.hvac": "HVAC Installation / Service",
  "contact.form.s.other": "Other / Not Sure",
  "contact.form.details": "Project Details",
  "contact.form.detailsPh": "Tell us a bit about your roof or solar project — age, square footage, timing, anything we should know.",
  "contact.form.photos": "Photos of Your Roof",
  "contact.form.optional": "(optional)",
  "contact.form.photosHelp": "Upload up to {n} photos. JPG, PNG or HEIC — max 8 MB each.",
  "contact.form.addPhoto": "Add Photo",
  "contact.form.removePhoto": "Remove photo",
  "contact.form.submit": "Submit Request",
  "contact.form.sending": "Sending your request...",
  "contact.form.privacy": "By submitting, you agree to be contacted about your project. We never share your info.",
  "contact.form.maxPhotos": "You can upload up to {n} photos.",
  "contact.form.notImage": "\"{n}\" is not an image.",
  "contact.form.tooLarge": "\"{n}\" is too large (max 8 MB).",
  "contact.form.error": "Something went wrong sending your request. Please call us at (505) 868-0847.",
  "contact.find.title": "Find Us",
  "contact.find.maps": "Open in Google Maps — Albuquerque, NM",
  "contact.cta.badge": "Ready When You Are",
  "contact.cta.title": "Prefer to Talk to a Real Person?",
  "contact.cta.body": "Skip the form — call us directly and one of our New Mexico-based estimators will walk you through your roofing or solar project, answer questions, and schedule your no-obligation on-site visit.",
  "contact.cta.email": "Email Our Team",
  "contact.success.title": "You're all set!",
  "contact.success.body1": "Thanks for reaching out. Our team just received your request and will contact you within",
  "contact.success.body2": "one business day",
  "contact.success.body3": ".",
  "contact.success.urgent": "Need it sooner? Call (505) 868-0847",
  "contact.success.gotIt": "Got it",
  "contact.success.back": "Back to home",
  "contact.photoAlt": "Roof photo {n}",
  "contact.requiredMark": "*",
  "contact.hero.callBtn": "Call (505) 868-0847",
  "contact.hero.emailBtn": "Email Us",
  "form.success": "Your request has been sent! We'll be in touch shortly.",
  "form.v.name": "Please enter your name",
  "form.v.phone": "Please enter a valid phone number",
  "form.v.email": "Please enter a valid email address",
  "form.v.address": "Please enter your project address",
  "form.v.generic": "Please check the form.",
  "contact.wizard.step": "Step {n} of {total}",
  "contact.wizard.s1": "Your Info",
  "contact.wizard.s2": "Project",
  "contact.wizard.s3": "Photos & Review",
  "contact.wizard.next": "Continue",
  "contact.wizard.back": "Back",
  "contact.wizard.review": "Review & Submit",
  "contact.wizard.reviewSub": "Double-check your details before sending.",
  "contact.wizard.noPhotos": "No photos added",

  // BLOG
  "blog.eyebrow": "Insights & Resources",
  "blog.indexTitle": "Roofing & Solar Insights",
  "blog.indexSub":
    "Expert advice, project guides and energy-saving tips from New Mexico's roofing & solar specialists.",
  "blog.searchPlaceholder": "Search articles...",
  "blog.loading": "Loading articles...",
  "blog.empty": "No articles match your filters.",
  "blog.clearFilters": "Clear filters",
  "blog.readArticle": "Read article",
  "blog.minRead": "min read",
  "blog.related": "Related Articles",
  "blog.recentTitle": "Recent Articles",
  "blog.recentSub": "Stay up to date with the latest from our roofing & solar experts.",
  "blog.viewAll": "View all articles",
  "blog.ctaTitle": "Ready to start your project?",
  "blog.ctaSub": "Get a no-obligation consultation from New Mexico's trusted roofing & solar team.",
  "blog.ctaPrimary": "Request an Estimate",
  "blog.cta.badge": "Talk to a Real Expert",
  "blog.cta.title": "Have Questions About Your Roof or Solar Project?",
  "blog.cta.body": "Our New Mexico-based roofing and solar specialists are happy to walk you through your options, answer questions from any article, and schedule a no-obligation on-site visit.",
  "blog.cat.all": "All",
  "blog.cat.roofing-tips": "Roofing Tips",
  "blog.cat.solar-energy": "Solar Energy",
  "blog.cat.roof-repairs": "Roof Repairs",
  "blog.cat.storm-damage": "Storm Damage",
  "blog.cat.energy-efficiency": "Energy Efficiency",
  "blog.cat.residential-roofing": "Residential Roofing",
  "blog.cat.commercial-roofing": "Commercial Roofing",

  // PROCESS / contact info
  "process.area":
    "Proudly based in Albuquerque, NM, and serving residential and commercial properties across the entire state of New Mexico.",
  "contact.hours": "Hours",
  "contact.hoursVal": "Monday – Friday, 7:00 AM – 7:00 PM",

  // FOOTER
  "footer.navigate": "Navigate",
  "footer.googleBusiness": "Google Business",
  "footer.rights": "All rights reserved.",
  "footer.tagline": "Roofing & Solar Integration across New Mexico.",
  "footer.poweredBy": "Powered by",

  // ACCESSIBILITY WIDGET
  "a11y.open": "Open accessibility menu",
  "a11y.close": "Close accessibility menu",
  "a11y.title": "Accessibility",
  "a11y.subtitle": "Adjust the site to your needs.",
  "a11y.contrast.label": "High contrast mode",
  "a11y.contrast.desc": "Black background, gold and white text.",
  "a11y.links.label": "Highlight links",
  "a11y.links.desc": "Underline and outline every link.",
  "a11y.textSize": "Text size",
  "a11y.size.base": "Default",
  "a11y.size.lg": "Large",
  "a11y.size.xl": "Larger",
  "a11y.size.xxl": "Largest",
  "a11y.size.aria": "Text size: {label}",
  "a11y.reset": "Reset to defaults",

  // TRAVEL FEE
  "travelFee.aria": "Travel fee disclosure",
  "travelFee.amount": "$100 travel fee",
  "travelFee.text": "applies to on-site estimates outside the Albuquerque metro area. The fee is fully credited back if you proceed with us.",

  // NM SERVICE AREA
  "nmArea.footerTitle": "New Mexico Service Area",
  "nmArea.title": "Proudly Serving All of New Mexico",
  "nmArea.sub": "Specialized roofing, solar, and HVAC services across every major city in New Mexico — from Albuquerque to the farthest corners of the state.",
  "nmArea.citiesAria": "Cities served in New Mexico",
  "nmArea.dontSee": "Don't see your town? We service the entire state — call",
  "nmArea.toConfirm": "to confirm coverage.",

  // GALLERY
  "gallery.eyebrow": "Project Gallery",
  "gallery.h1": "Before & After Results",
  "gallery.sub": "Real roofing, solar, and HVAC transformations across New Mexico. Every project is executed by our elite, vetted crews — to manufacturer spec, on schedule.",
  "gallery.callBtn": "Call (505) 868-0847",
  "gallery.requestBtn": "Request Consultation",
  "gallery.before": "Before",
  "gallery.after": "After",
  "gallery.dragCompare": "Drag to compare before and after",
  "gallery.noteTitle": "Mobile-friendly uploads coming soon.",
  "gallery.noteBody": "This gallery layout is designed for fast before-and-after additions.",
  "gallery.ctaTitle": "Ready for your before & after?",
  "gallery.ctaBody": "Request a consultation today — our specialists will assess your home and walk you through every option.",
  "gallery.cat.roofing": "Roofing",
  "gallery.cat.solar": "Solar",
  "gallery.cat.hvac": "HVAC",

  // ABOUT — credentials
  "about.creds.title": "Our Credentials",
  "about.creds.sub": "Decades of experience, proper licensing, and a trusted network of specialized trades — built to protect your investment.",
  "about.creds.aria": "Our credentials",
  "about.creds.years": "25 Years",
  "about.creds.yearsKicker": "of Industry Experience",
  "about.creds.yearsBody": "A quarter-century of roofing, solar, and HVAC craftsmanship across New Mexico — from single-family homes to large commercial installs.",
  "about.creds.licensed": "Licensed since 2022",
  "about.creds.licensedKicker": "Fully Licensed, Bonded & Insured",
  "about.creds.licensedBody": "AMR 3 Design & Construction LLC operates under a current New Mexico contractor's license — every project carries proper documentation, permits, and insurance.",
  "about.creds.network": "Elite Vetted Network",
  "about.creds.networkKicker": "Trusted Specialized Crews",
  "about.creds.networkBody": "We operate through an elite network of trusted, vetted subcontractors — each one specialized in roofing, solar, or HVAC. Faster mobilization, sharper expertise, and a single point of accountability: us.",
};

const es: Dict = {
  // NAV
  "nav.home": "Inicio",
  "nav.services": "Servicios",
  "nav.about": "Nosotros",
  "nav.work": "Proyectos",
  "nav.faq": "Preguntas",
  "nav.blog": "Blog",
  "nav.contact": "Contacto",
  "nav.callNow": "Llámenos",
  "nav.requestConsultation": "Solicitar Consulta",
  "nav.callFull": "Llamar (505) 868-0847",

  // COMMON
  "common.callNow": "Llámenos",
  "common.callPhone": "Llamar (505) 868-0847",
  "common.requestConsultation": "Solicitar Consulta",
  "common.getFreeEstimate": "Obtenga un Estimado",
  "common.requestQuote": "Solicitar Cotización",
  "common.startProject": "Inicie su Proyecto",
  "common.viewServices": "Ver Nuestros Servicios",
  "common.readyTransform": "¿Listo para transformar su techo?",
  "home.cta.title": "¿Listo para transformar su techo?",
  "work.cta.title": "¿Le gusta lo que ve? Construyamos el suyo.",
  "about.cta.title": "¿Listo para trabajar con un equipo de confianza?",
  "faq.cta.title": "¿Aún tiene preguntas? Estamos para ayudar.",
  "common.emailUs": "Escríbanos",
  "common.licensedInsured": "Con Licencia y Asegurados",
  "common.manufacturerWarranties": "Garantías del Fabricante",
  "common.emergency247": "Emergencias 24/7",
  "common.yearsBadge": "25+ Años • Nuevo México",
  "common.ourStory": "Nuestra Historia",

  // HOME — about section
  "home.about.badge": "Acerca de AMR 3",
  "home.about.title": "Sobre Nosotros",
  "home.about.sub": "Un socio de confianza para techado, solar y HVAC en Nuevo México.",
  "home.about.body":
    "Un socio de confianza, operado por sus dueños, para techado y solar en Nuevo México — entregando calidad, responsabilidad y valor a largo plazo desde la consulta hasta la finalización.",

  // HERO
  "hero.h1": "Expertos en Techos e Integración Solar en Nuevo México",
  "hero.sub":
    "Proteja su hogar, reduzca costos de energía y aumente el valor de su propiedad con nuestras soluciones integrales de techado y energía solar. Más de 25 años de artesanía confiable.",
  "hero.cta1": "Solicitar Consulta",
  "hero.cta2": "Llamar 1-505-868-0847",

  // HOME services
  "home.services.title": "Nuestros Servicios",
  "home.services.sub": "Soluciones completas de techado y solar — todo bajo un solo contratista de confianza.",
  "home.services.learnAbout": "Conozca más sobre",
  "home.svc.1.title": "Instalación y Reemplazo de Techos",
  "home.svc.1.body": "Sistemas premium de asfalto, metal, teja y TPO diseñados para el clima de Nuevo México, instalados para durar décadas.",
  "home.svc.2.title": "Reparaciones y Mantenimiento de Techos",
  "home.svc.2.body": "Reparaciones rápidas y planes de mantenimiento estacional que protegen su techo y prolongan su vida útil.",
  "home.svc.3.title": "Instalación de Paneles Solares",
  "home.svc.3.body": "Paneles e inversores de alta eficiencia diseñados y permitidos internamente para máximo ahorro y rendimiento.",
  "home.svc.4.title": "Techado Solar Integrado",
  "home.svc.4.body": "Una sola construcción coordinada para techo y solar — menor costo, menos demoras y una sola garantía.",
  "home.svc.5.title": "Daños por Tormenta y Emergencias",
  "home.svc.5.body": "Respuesta de emergencia 24/7, lonas de protección y restauración completa con asistencia de seguros.",
  "home.svc.6.title": "Canaletas y Reclamos de Seguro",
  "home.svc.6.body": "Instalación de canaletas sin junturas y soporte completo de reclamos de seguro de principio a fin.",
  "home.svc.7.title": "Instalación y Servicio HVAC",
  "home.svc.7.body": "Sistemas eficientes de calefacción, refrigeración y ventilación — instalados, reparados y ajustados para el clima desértico de Nuevo México.",

  // HOME process
  "home.how.title": "Cómo Trabajamos",
  "home.how.sub": "Desde la primera consulta hasta la activación de la garantía, hacemos el techado y solar simple.",
  "home.step.1.title": "Consulta y Visita en Sitio",
  "home.step.1.body": "Nos reunimos en sitio, escuchamos sus metas y evaluamos sus necesidades de techo y solar — sin presión, sin obligación.",
  "home.step.2.title": "Propuesta y Estimado Detallado",
  "home.step.2.body": "Recibe una propuesta clara con materiales, cronograma, términos de garantía y precios transparentes.",
  "home.step.3.title": "Permisos y Preparación",
  "home.step.3.body": "Manejamos cada permiso, presentación a HOA e interconexión con la utilidad para que su proyecto avance sin problemas.",
  "home.step.4.title": "Instalación Sin Complicaciones",
  "home.step.4.body": "Nuestros equipos internos de techado y solar ejecutan la obra a tiempo, dentro del presupuesto y según especificaciones del fabricante.",
  "home.step.5.title": "Inspección Final y Limpieza",
  "home.step.5.body": "Recorremos el proyecto con usted, verificamos cada detalle y dejamos la propiedad más limpia de como la encontramos.",
  "home.step.6.title": "Activación de Garantía y Soporte",
  "home.step.6.body": "Las garantías de mano de obra y del fabricante se activan y permanecemos disponibles a largo plazo.",

  // HOME why
  "home.why.title": "¿Por Qué Elegir AMR 3?",
  "home.why.sub": "Entregamos techos y solar premium con la artesanía, responsabilidad y garantías que las familias de Nuevo México merecen.",
  "home.why.1.title": "25+ Años de Experiencia en NM",
  "home.why.1.body": "Décadas de trabajo práctico en techado y solar en Albuquerque y todo el estado de Nuevo México.",
  "home.why.2.title": "Con Licencia, Fianza y Seguro",
  "home.why.2.body": "Contratista de Nuevo México totalmente licenciado con cobertura completa de responsabilidad civil y compensación laboral.",
  "home.why.3.title": "Calidad Operada por sus Dueños",
  "home.why.3.body": "Cada proyecto es supervisado por un dueño — nunca delegado a subcontratistas que nunca conoció.",
  "home.why.4.title": "Instalador Certificado por el Fabricante",
  "home.why.4.body": "Certificados para instalar sistemas premium de techado y solar con respaldo total de garantía del fabricante.",
  "home.why.5.title": "Garantía Sin Filtraciones y de Rendimiento",
  "home.why.5.body": "Garantía de mano de obra en cada techo y garantía de rendimiento en cada sistema solar que instalamos.",

  // HOME reviews
  "home.reviews.title": "Reseñas en Google",
  "home.reviews.sub": "Vea lo que dicen los propietarios de todo Nuevo México sobre trabajar con AMR 3.",
  "home.reviews.seeAll": "Ver todas las reseñas en Google",

  // HOME work
  "home.work.title": "Vea Nuestro Trabajo en Acción",
  "home.work.body1": "Vea cómo nuestro equipo entrega proyectos premium de techado y solar en todo Nuevo México — desde el primer retiro hasta la activación final del panel. Equipos reales, hogares reales, artesanía real.",
  "home.work.body2": "Tratamos cada proyecto como si fuera nuestro propio hogar — esa es la diferencia AMR 3, y se nota en cada techo y sistema solar que instalamos.",
  "home.work.playLabel": "Reproducir video del proyecto",

  // HOME location
  "home.loc.title": "Nuestra Ubicación",

  // ABOUT
  "about.badge": "Operado por sus Dueños · 25+ Años en NM",
  "about.h1": "Acerca de AMR 3 Design & Construction",
  "about.intro": "AMR 3 Design & Construction LLC fue fundada para ofrecer a los propietarios y empresas de Nuevo México un enfoque profesional y simplificado al techado y a la integración solar. En lugar de coordinar varios contratistas, los clientes trabajan con un solo equipo experimentado y operado por sus dueños desde la primera consulta hasta la activación final de la garantía.",
  "about.story.title": "Nuestra Historia",
  "about.story.sub": "De una única visión a un nombre confiable en techado y solar en Nuevo México.",
  "about.story.1.lead": "AMR 3 Design & Construction LLC",
  "about.story.1.body": "fue fundada para ofrecer a los propietarios y empresas de Nuevo México un único socio confiable para techado y solar premium — sin coordinar contratistas, sin garantías desajustadas, sin sorpresas.",
  "about.story.2.lead": "Lo que nos distingue es nuestro enfoque operado por sus dueños.",
  "about.story.2.body": "Cada proyecto es supervisado por un dueño desde el estimado hasta el recorrido final — los clientes trabajan directamente con quienes ejecutan la obra, no con un intermediario.",
  "about.story.3.lead": "Nos enfocamos en materiales premium, artesanía certificada y comunicación clara",
  "about.story.3.body": "para que los clientes inviertan con confianza en su techo y sistema solar. Nuestro proceso integrado entrega un resultado bello y eficiente, hecho para durar décadas en el clima de Nuevo México.",
  "about.story.paragraph": "AMR 3 Design & Construction LLC fue fundada para ofrecer a los propietarios y empresas de Nuevo México un único socio confiable para techado y solar premium — sin coordinar contratistas, sin garantías desajustadas, sin sorpresas. Cada proyecto es operado por sus dueños y supervisado desde el estimado hasta el recorrido final, así trabaja directamente con quienes ejecutan la obra. Nos enfocamos en materiales premium, artesanía certificada y comunicación clara, entregando un resultado bello y eficiente, hecho para durar décadas en el clima de Nuevo México.",
  "about.story.badges.years": "25+ Años de Experiencia",
  "about.story.badges.owner": "Operado por sus Dueños",
  "about.story.badges.licensed": "Con Licencia",
  "about.story.badges.bonded": "Con Fianza",
  "about.story.badges.insured": "Asegurado",
  "about.apart.title": "Lo Que Nos Distingue",
  "about.apart.sub": "Hay muchas empresas de techado y solar. Aquí está por qué los propietarios y empresas de Nuevo México eligen a AMR 3.",
  "about.apart.1.title": "Techado y Solar Todo en Uno",
  "about.apart.1.body": "En lugar de coordinar techadores, instaladores solares y electricistas separados, recibe un equipo experimentado que se encarga de techo, solar y acabado de principio a fin.",
  "about.apart.2.title": "Calidad Operada por sus Dueños",
  "about.apart.2.body": "Cada proyecto es liderado por un dueño — nunca delegado a subcontratistas desconocidos. Los mismos estándares en cada trabajo, siempre.",
  "about.apart.3.title": "25+ Años de Experiencia en NM",
  "about.apart.3.body": "Décadas de trabajo práctico en techado y solar en Albuquerque, Rio Rancho, Santa Fe y el resto de Nuevo México — hechos para nuestro clima.",
  "about.apart.4.title": "Con Licencia, Fianza y Seguro",
  "about.apart.4.body": "Contratista totalmente licenciado en Nuevo México con cobertura completa de responsabilidad civil y compensación laboral en cada proyecto.",
  "about.apart.5.title": "Especialistas en Solar Integrado",
  "about.apart.5.body": "Instaladores solares certificados que diseñan, permiten e instalan su sistema como una sola obra coordinada con su techo.",
  "about.commit.title": "Nuestro Compromiso y Experiencia",
  "about.commit.cs.title": "Enfocados en la Satisfacción del Cliente",
  "about.commit.cs.body": "Respaldamos cada proyecto con mano de obra certificada, estimados precisos por escrito, programación coordinada y materiales premium diseñados para el clima de Nuevo México.",
  "about.commit.partner.lead": "Las alianzas con fabricantes",
  "about.commit.partner.body": "líderes en techado y solar aseguran sistemas de la más alta calidad y respaldo total de garantía en cada instalación.",
  "about.commit.roof.title": "Experiencia en Techos",
  "about.commit.roof.body": "Experiencia especializada en instalación residencial y comercial.",
  "about.commit.solar.title": "Integración Solar",
  "about.commit.solar.body": "Especialistas solares certificados en quienes puede confiar.",

  // SERVICES
  "svc.badge": "Especialistas en Techado y Solar",
  "svc.h1.l1": "Servicios de Techado y Solar en",
  "svc.h1.l2": "Albuquerque y Nuevo México",
  "svc.intro": "Desde instalaciones premium de techos e integración solar hasta restauración por tormentas y canaletas — ofrecemos una experiencia constructiva ágil con artesanía profesional. Servimos Albuquerque, Rio Rancho, Santa Fe, Los Lunas y comunidades cercanas en Nuevo México.",
  "svc.primary.title": "Servicios Principales",
  "svc.primary.sub": "Nuestras soluciones principales de techado y solar — diseñadas, permitidas e instaladas internamente para hogares y negocios de Nuevo México.",
  "svc.expect": "Qué Esperar:",
  "svc.p.1.title": "Instalación y Reemplazo de Techos",
  "svc.p.1.body": "Desde tejas premium de asfalto hasta sistemas de metal, teja y TPO — diseñamos, permitimos e instalamos techos hechos para durar décadas en el sol, viento y monzón de Nuevo México.",
  "svc.p.1.expect": "Cada proyecto comienza con una evaluación en sitio sin compromiso donde revisamos su techo actual, discutimos opciones de materiales y entregamos una propuesta clara por escrito. Nuestros equipos internos manejan retiro, plataforma, base, flashing y acabado — respaldados por garantías del fabricante y de mano de obra.",
  "svc.p.2.title": "Instalación e Integración Solar",
  "svc.p.2.body": "Sistemas solares de alta eficiencia diseñados, permitidos e instalados por el mismo equipo que construye su techo — para un acabado perfecto, menor costo total y un solo responsable de garantía.",
  "svc.p.2.expect": "Comenzamos con una auditoría energética sin costo y un diseño personalizado según su consumo, orientación del techo y presupuesto. Coordinamos la interconexión con la utilidad, todos los permisos y trámites de HOA, e instalamos paneles, inversores y monitoreo premium con un solo punto de responsabilidad.",
  "svc.p.3.title": "Daños por Tormenta y Reparación de Emergencia",
  "svc.p.3.body": "Respuesta 24/7, lonas profesionales y restauración completa después de daños por granizo, viento o monzón — manejada de principio a fin para proteger su hogar y agilizar su reclamo.",
  "svc.p.3.expect": "Respondemos rápido, documentamos los daños con fotos e informes detallados, instalamos protección de emergencia y coordinamos directamente con su ajustador de seguros. Nuestro equipo gestiona toda la restauración para que tenga un solo punto de contacto desde la primera llamada hasta la inspección final.",
  "svc.add.title": "Servicios Adicionales",
  "svc.add.sub": "Más allá de techos y solar — también atendemos canaletas, reclamos de seguro y sistemas comerciales.",
  "svc.add.1.title": "Canaletas Sin Junturas",
  "svc.add.1.body": "Canaletas de aluminio sin junturas hechas a medida, bajantes y protectores de hojas — instaladas y mantenidas para alejar el agua de su techo y cimentación.",
  "svc.add.2.title": "Asistencia con Reclamos de Seguro",
  "svc.add.2.body": "Documentamos daños, nos reunimos con su ajustador, presentamos los papeles y facturamos al seguro directamente — quitando el estrés del proceso.",
  "svc.add.3.title": "Techado Comercial y Especializado",
  "svc.add.3.body": "Techos planos, sistemas TPO, retechado comercial, claraboyas y detalles arquitectónicos personalizados para empresas en todo Nuevo México.",
  "svc.add.4.title": "Instalación y Servicio HVAC",
  "svc.add.4.body": "Servicio HVAC completo: aire acondicionado de alta eficiencia, bombas de calor, calefacciones y ductos — diseñados, instalados y mantenidos para el clima de Nuevo México.",
  "svc.coord": "Programación coordinada — un solo equipo maneja techo, solar y acabados.",
  "svc.commit.title": "Nuestro Compromiso",
  "svc.commit.body1": "Cada proyecto incluye nuestro compromiso con",
  "svc.commit.body1b": "artesanía licenciada, estimados precisos y materiales premium",
  "svc.commit.body1c": ". Nos enfocamos en la satisfacción del cliente en cada etapa.",
  "svc.commit.quote": "\"Programación coordinada y comunicación clara — para que disfrute del resultado, no del estrés.\"",
  "svc.chip.years": "25+ Años de Experiencia en NM",
  "svc.chip.licensed": "Con Licencia, Fianza y Seguro",
  "svc.chip.certified": "Certificado por el Fabricante",
  "svc.cta.title": "Cuéntenos sobre su proyecto de techado o solar — responderemos rápido.",

  // FAQ
  "faq.badge": "Respuestas · Techado y Solar",
  "faq.h1": "Preguntas Frecuentes",
  "faq.sub": "Todo lo que necesita saber sobre trabajar con AMR 3 Design & Construction LLC en proyectos de techado y solar en todo Nuevo México.",
  "faq.still": "¿Aún tiene preguntas?",

  // WORK
  "work.badge": "Portafolio de Proyectos",
  "work.h1": "Nuestro Trabajo",
  "work.intro": "Vea proyectos reales completados por AMR 3 Design & Construction — techado residencial, integraciones solares y sistemas comerciales en Albuquerque, Rio Rancho, Santa Fe y el resto de Nuevo México.",
  "work.tab.all": "Todos",
  "work.tab.roofing": "Techado",
  "work.tab.solar": "Solar",
  "work.tab.repairs": "Reparaciones",
  "work.tab.commercial": "Comercial",
  "work.galleryTitle": "Galería de Proyectos",
  "work.gallerySub": "Explore una selección curada de nuestros proyectos de techado y solar completados en Nuevo México.",
  "work.baTitle": "Antes y Después",
  "work.baSub": "Deslice para ver la transformación real en hogares que hemos reparado, reemplazado y mejorado.",

  // CONTACT
  "contact.badge": "Consulta · Sin Obligación",
  "contact.h1": "Solicite una Consulta",
  "contact.intro": "Complete el formulario o llámenos directamente. Servimos Albuquerque y comunidades cercanas de Nuevo México incluyendo Rio Rancho, Santa Fe, Los Lunas, Las Cruces y las East Mountains.",
  "contact.info.address": "Albuquerque, NM 87113",
  "contact.info.hours": "Lun–Vie 7AM–7PM · Emergencias 24/7",
  "contact.form.title": "Cuéntenos Sobre su Proyecto",
  "contact.form.sub": "Una persona real le responderá en un día hábil.",
  "contact.form.name": "Nombre",
  "contact.form.namePh": "Su nombre completo",
  "contact.form.phone": "Teléfono",
  "contact.form.email": "Correo Electrónico",
  "contact.form.address": "Dirección del Proyecto",
  "contact.form.addressPh": "Calle, Ciudad, NM",
  "contact.form.service": "Tipo de Servicio",
  "contact.form.servicePh": "Elija un servicio (opcional)",
  "contact.form.s.roofing": "Instalación / Reemplazo de Techo",
  "contact.form.s.solar": "Instalación de Paneles Solares",
  "contact.form.s.both": "Techo + Solar Integrado",
  "contact.form.s.repair": "Reparación / Mantenimiento de Techo",
  "contact.form.s.storm": "Daños por Tormenta / Reclamo de Seguro",
  "contact.form.s.hvac": "Instalación / Servicio HVAC",
  "contact.form.s.other": "Otro / No estoy seguro",
  "contact.form.details": "Detalles del Proyecto",
  "contact.form.detailsPh": "Cuéntenos un poco sobre su techo o proyecto solar — edad, metraje, tiempos, lo que debamos saber.",
  "contact.form.photos": "Fotos de su Techo",
  "contact.form.optional": "(opcional)",
  "contact.form.photosHelp": "Suba hasta {n} fotos. JPG, PNG o HEIC — máximo 8 MB cada una.",
  "contact.form.addPhoto": "Agregar Foto",
  "contact.form.removePhoto": "Quitar foto",
  "contact.form.submit": "Enviar Solicitud",
  "contact.form.sending": "Enviando su solicitud...",
  "contact.form.privacy": "Al enviar, acepta ser contactado sobre su proyecto. Nunca compartimos su información.",
  "contact.form.maxPhotos": "Puede subir hasta {n} fotos.",
  "contact.form.notImage": "\"{n}\" no es una imagen.",
  "contact.form.tooLarge": "\"{n}\" es demasiado grande (máx 8 MB).",
  "contact.form.error": "Algo salió mal al enviar su solicitud. Por favor llámenos al (505) 868-0847.",
  "contact.find.title": "Encuéntrenos",
  "contact.find.maps": "Abrir en Google Maps — Albuquerque, NM",
  "contact.cta.badge": "Listos Cuando Usted Lo Esté",
  "contact.cta.title": "¿Prefiere Hablar con una Persona Real?",
  "contact.cta.body": "Sáltese el formulario — llámenos directamente y uno de nuestros estimadores en Nuevo México le guiará en su proyecto, responderá preguntas y agendará su visita en sitio sin compromiso.",
  "contact.cta.email": "Escriba a Nuestro Equipo",
  "contact.success.title": "¡Listo!",
  "contact.success.body1": "Gracias por contactarnos. Nuestro equipo recibió su solicitud y le contactará en",
  "contact.success.body2": "un día hábil",
  "contact.success.body3": ".",
  "contact.success.urgent": "¿Lo necesita antes? Llame al (505) 868-0847",
  "contact.success.gotIt": "Entendido",
  "contact.success.back": "Volver al inicio",
  "contact.photoAlt": "Foto del techo {n}",
  "contact.requiredMark": "*",
  "contact.hero.callBtn": "Llamar (505) 868-0847",
  "contact.hero.emailBtn": "Escríbanos",
  "form.success": "¡Su solicitud ha sido enviada! Le contactaremos pronto.",
  "form.v.name": "Por favor ingrese su nombre",
  "form.v.phone": "Por favor ingrese un teléfono válido",
  "form.v.email": "Por favor ingrese un correo válido",
  "form.v.address": "Por favor ingrese la dirección del proyecto",
  "form.v.generic": "Por favor revise el formulario.",
  "contact.wizard.step": "Paso {n} de {total}",
  "contact.wizard.s1": "Sus Datos",
  "contact.wizard.s2": "Proyecto",
  "contact.wizard.s3": "Fotos y Revisión",
  "contact.wizard.next": "Continuar",
  "contact.wizard.back": "Atrás",
  "contact.wizard.review": "Revisar y Enviar",
  "contact.wizard.reviewSub": "Verifique sus datos antes de enviar.",
  "contact.wizard.noPhotos": "Sin fotos",

  // BLOG
  "blog.eyebrow": "Recursos e Información",
  "blog.indexTitle": "Información sobre Techos y Solar",
  "blog.indexSub":
    "Consejos expertos, guías de proyectos y ahorro energético de los especialistas en techos y solar de Nuevo México.",
  "blog.searchPlaceholder": "Buscar artículos...",
  "blog.loading": "Cargando artículos...",
  "blog.empty": "Ningún artículo coincide con sus filtros.",
  "blog.clearFilters": "Limpiar filtros",
  "blog.readArticle": "Leer artículo",
  "blog.minRead": "min de lectura",
  "blog.related": "Artículos Relacionados",
  "blog.recentTitle": "Artículos Recientes",
  "blog.recentSub": "Manténgase al día con lo último de nuestros expertos en techos y solar.",
  "blog.viewAll": "Ver todos los artículos",
  "blog.ctaTitle": "¿Listo para iniciar su proyecto?",
  "blog.ctaSub": "Obtenga una consulta sin compromiso del equipo de techos y solar de confianza en Nuevo México.",
  "blog.ctaPrimary": "Solicitar Estimado",
  "blog.cta.badge": "Hable con un Experto Real",
  "blog.cta.title": "¿Tiene Preguntas Sobre su Techo o Proyecto Solar?",
  "blog.cta.body": "Nuestros especialistas en techado y solar de Nuevo México lo guiarán por sus opciones, responderán preguntas de cualquier artículo y agendarán una visita en sitio sin compromiso.",
  "blog.cat.all": "Todos",
  "blog.cat.roofing-tips": "Consejos de Techos",
  "blog.cat.solar-energy": "Energía Solar",
  "blog.cat.roof-repairs": "Reparaciones de Techo",
  "blog.cat.storm-damage": "Daños por Tormenta",
  "blog.cat.energy-efficiency": "Eficiencia Energética",
  "blog.cat.residential-roofing": "Techos Residenciales",
  "blog.cat.commercial-roofing": "Techos Comerciales",

  // PROCESS / contact info
  "process.area":
    "Con orgullo basados en Albuquerque, NM, sirviendo a propiedades residenciales y comerciales en todo el estado de Nuevo México.",
  "contact.hours": "Horario",
  "contact.hoursVal": "Lunes – Viernes, 7:00 AM – 7:00 PM",

  // FOOTER
  "footer.navigate": "Navegación",
  "footer.googleBusiness": "Google Business",
  "footer.rights": "Todos los derechos reservados.",
  "footer.tagline": "Techos e Integración Solar en todo Nuevo México.",
  "footer.poweredBy": "Desarrollado por",

  // ACCESSIBILITY WIDGET
  "a11y.open": "Abrir menú de accesibilidad",
  "a11y.close": "Cerrar menú de accesibilidad",
  "a11y.title": "Accesibilidad",
  "a11y.subtitle": "Adapte el sitio a sus necesidades.",
  "a11y.contrast.label": "Modo de alto contraste",
  "a11y.contrast.desc": "Fondo negro, texto dorado y blanco.",
  "a11y.links.label": "Resaltar enlaces",
  "a11y.links.desc": "Subraya y delinea todos los enlaces.",
  "a11y.textSize": "Tamaño del texto",
  "a11y.size.base": "Predeterminado",
  "a11y.size.lg": "Grande",
  "a11y.size.xl": "Más grande",
  "a11y.size.xxl": "Máximo",
  "a11y.size.aria": "Tamaño del texto: {label}",
  "a11y.reset": "Restablecer",

  // TRAVEL FEE
  "travelFee.aria": "Aviso de tarifa de desplazamiento",
  "travelFee.amount": "Tarifa de $100",
  "travelFee.text": "se aplica a visitas en sitio fuera del área metropolitana de Albuquerque. La tarifa se acredita íntegramente si nos contrata.",

  // NM SERVICE AREA
  "nmArea.footerTitle": "Área de Servicio en Nuevo México",
  "nmArea.title": "Servimos con Orgullo Todo Nuevo México",
  "nmArea.sub": "Servicios especializados de techos, solar y HVAC en cada ciudad importante de Nuevo México — desde Albuquerque hasta los rincones más lejanos del estado.",
  "nmArea.citiesAria": "Ciudades atendidas en Nuevo México",
  "nmArea.dontSee": "¿No ve su pueblo? Servimos todo el estado — llame al",
  "nmArea.toConfirm": "para confirmar cobertura.",

  // GALLERY
  "gallery.eyebrow": "Galería de Proyectos",
  "gallery.h1": "Resultados Antes y Después",
  "gallery.sub": "Transformaciones reales de techos, solar y HVAC en todo Nuevo México. Cada proyecto es ejecutado por nuestros equipos élite y certificados — según especificaciones del fabricante y a tiempo.",
  "gallery.callBtn": "Llamar (505) 868-0847",
  "gallery.requestBtn": "Solicitar Consulta",
  "gallery.before": "Antes",
  "gallery.after": "Después",
  "gallery.dragCompare": "Arrastre para comparar antes y después",
  "gallery.noteTitle": "Carga desde móvil próximamente.",
  "gallery.noteBody": "Esta galería está diseñada para añadir rápidamente pares de antes y después.",
  "gallery.ctaTitle": "¿Listo para su antes y después?",
  "gallery.ctaBody": "Solicite una consulta hoy — nuestros especialistas evaluarán su hogar y le guiarán en cada opción.",
  "gallery.cat.roofing": "Techos",
  "gallery.cat.solar": "Solar",
  "gallery.cat.hvac": "HVAC",

  // ABOUT — credentials
  "about.creds.title": "Nuestras Credenciales",
  "about.creds.sub": "Décadas de experiencia, licencias vigentes y una red de oficios especializados de confianza — todo para proteger su inversión.",
  "about.creds.aria": "Nuestras credenciales",
  "about.creds.years": "25 Años",
  "about.creds.yearsKicker": "de Experiencia en la Industria",
  "about.creds.yearsBody": "Un cuarto de siglo de oficio en techos, solar y HVAC en todo Nuevo México — desde viviendas unifamiliares hasta grandes instalaciones comerciales.",
  "about.creds.licensed": "Licenciados desde 2022",
  "about.creds.licensedKicker": "Totalmente Licenciados, Afianzados y Asegurados",
  "about.creds.licensedBody": "AMR 3 Design & Construction LLC opera con una licencia vigente de contratista de Nuevo México — cada proyecto cuenta con la documentación, permisos y seguros adecuados.",
  "about.creds.network": "Red Élite Certificada",
  "about.creds.networkKicker": "Equipos Especializados de Confianza",
  "about.creds.networkBody": "Operamos a través de una red élite de subcontratistas certificados — cada uno especializado en techos, solar o HVAC. Movilización más rápida, experiencia más aguda y un único punto de responsabilidad: nosotros.",
};

const dicts: Record<Lang, Dict> = { en, es };

type TFn = (k: string, vars?: Record<string, string | number>) => string;

const Ctx = createContext<{ lang: Lang; setLang: (l: Lang) => void; t: TFn }>({
  lang: "en",
  setLang: () => {},
  t: (k) => k,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>("en");

  useEffect(() => {
    const stored = (typeof window !== "undefined" &&
      localStorage.getItem("amr3-lang")) as Lang | null;
    if (stored === "en" || stored === "es") setLangState(stored);
  }, []);

  const setLang = (l: Lang) => {
    setLangState(l);
    if (typeof window !== "undefined") localStorage.setItem("amr3-lang", l);
  };

  const t: TFn = (k, vars) => {
    let s = dicts[lang][k] ?? dicts.en[k] ?? k;
    if (vars) {
      for (const [vk, vv] of Object.entries(vars)) {
        s = s.replace(new RegExp(`\\{${vk}\\}`, "g"), String(vv));
      }
    }
    return s;
  };
  return <Ctx.Provider value={{ lang, setLang, t }}>{children}</Ctx.Provider>;
}

export const useI18n = () => useContext(Ctx);
